import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { PartFile } from './types.d';
import { deletePartFile, getPartFileUrls, uploadPartFile } from './service';
import { error } from '../legacy/flash-messenger';
import { ALLOWED_TYPES, MAX_FILE_COUNT, MAX_FILE_SIZE } from './constants';

export type PartFileStatus = 'saved' | 'session' | 'uploading' | 'pending-delete';

export interface TrackedPartFile {
  key: string;
  id?: number;
  fileName: string;
  fileSize?: number;
  createdAt?: string;
  status: PartFileStatus;
}

interface UploadingFile {
  key: string;
  fileName: string;
}

export interface UsePartFiles {
  files: TrackedPartFile[];
  loading: boolean;
  loadError: boolean;
  uploadingCount: number;
  /** True while any upload or immediate delete is in flight — used to block modal close. */
  isBusy: boolean;
  canAddMore: boolean;
  uploadFiles: (incoming: File[]) => Promise<void>;
  requestDelete: (file: TrackedPartFile) => Promise<void>;
  restore: (id: number) => void;
  /** Fire the deferred deletes accumulated this session (call on Save). */
  commit: () => Promise<void>;
  /** Delete every file uploaded this session (call on Cancel/close). */
  rollback: () => Promise<void>;
  /** Re-fetch a fresh presigned URL for one file (URLs expire in 15 min). */
  getFreshUrl: (id: number) => Promise<string>;
}

const localKey = (f: File): string => `${f.name}-${f.size}-${f.lastModified}`;
const dedupeKey = (name: string, size?: number): string => `${name}-${size ?? ''}`;

/**
 * Owns the transactional file state for a part inside the edit/usage modal:
 * eager uploads that roll back on cancel, and deletes of previously-saved files
 * that are deferred until save. Instantiated once in Modal and threaded down to
 * the Photos component.
 */
export default function usePartFiles(
  workOrderId: number,
  partId: number,
  userRole: string,
  enabled: boolean,
): UsePartFiles {
  const [serverFiles, setServerFiles] = useState<PartFile[]>([]);
  const [sessionIds, setSessionIds] = useState<number[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [deletingCount, setDeletingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    if (!enabled || !partId) {
      setServerFiles([]);
      return undefined;
    }
    setLoading(true);
    getPartFileUrls(workOrderId, partId, userRole)
      .then((files) => {
        if (active) {
          setServerFiles(files);
          setLoadError(false);
        }
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enabled, partId, workOrderId, userRole]);

  const refresh = useCallback(async () => {
    try {
      const files = await getPartFileUrls(workOrderId, partId, userRole);
      setServerFiles(files);
      setLoadError(false);
    } catch (_) {
      setLoadError(true);
    }
  }, [workOrderId, partId, userRole]);

  const uploadFiles = useCallback(
    async (incoming: File[]): Promise<void> => {
      const remaining = MAX_FILE_COUNT - serverFiles.length - uploading.length;
      if (remaining <= 0) {
        error(`A part can have at most ${MAX_FILE_COUNT} files.`);
        return;
      }

      const seen = new Set([
        ...serverFiles.map((f) => dedupeKey(f.file_name, f.file_size)),
        ...uploading.map((u) => u.key),
      ]);
      const rejects: string[] = [];
      const candidates = incoming.filter((f) => {
        if (!ALLOWED_TYPES.has(f.type)) {
          rejects.push(`${f.name}: unsupported file type`);
          return false;
        }
        if (f.size > MAX_FILE_SIZE) {
          rejects.push(`${f.name}: exceeds the maximum size of 20MB`);
          return false;
        }
        if (seen.has(dedupeKey(f.name, f.size)) || seen.has(localKey(f))) {
          return false;
        }
        // Track accepted files so a duplicate later in the same batch is rejected too.
        seen.add(dedupeKey(f.name, f.size));
        seen.add(localKey(f));
        return true;
      });
      if (rejects.length > 0) {
        error(rejects.join('. '));
      }

      const accepted = candidates.slice(0, remaining);
      if (candidates.length > accepted.length) {
        error(`A part can have at most ${MAX_FILE_COUNT} files.`);
      }
      if (accepted.length === 0) {
        return;
      }

      const placeholders = accepted.map((f) => ({ key: localKey(f), fileName: f.name }));
      setUploading((prev) => [...prev, ...placeholders]);

      const newIds: number[] = [];
      await accepted.reduce(async (chain, f) => {
        await chain;
        try {
          const uploaded = await uploadPartFile(workOrderId, partId, f, userRole);
          newIds.push(uploaded.id);
        } catch (err) {
          error((err as Error)?.message || `Failed to upload ${f.name}.`);
        } finally {
          setUploading((prev) => prev.filter((u) => u.key !== localKey(f)));
        }
      }, Promise.resolve());

      if (newIds.length > 0) {
        setSessionIds((prev) => [...prev, ...newIds]);
        await refresh();
      }
    },
    [serverFiles, uploading, workOrderId, partId, userRole, refresh],
  );

  const requestDelete = useCallback(
    async (file: TrackedPartFile): Promise<void> => {
      if (file.id === undefined) {
        return;
      }
      const { id } = file;
      // Files uploaded this session are removed immediately (frees the quota).
      if (file.status === 'session') {
        setDeletingCount((c) => c + 1);
        try {
          await deletePartFile(workOrderId, partId, id, userRole);
          setServerFiles((prev) => prev.filter((f) => f.id !== id));
          setSessionIds((prev) => prev.filter((sid) => sid !== id));
        } catch (err) {
          error((err as Error)?.message || 'Failed to delete file.');
        } finally {
          setDeletingCount((c) => c - 1);
        }
        return;
      }
      // Previously-saved files are only marked for deletion; the API fires on save.
      setPendingDeleteIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [workOrderId, partId, userRole],
  );

  const restore = useCallback((id: number) => {
    setPendingDeleteIds((prev) => prev.filter((pid) => pid !== id));
  }, []);

  const commit = useCallback(async (): Promise<void> => {
    if (pendingDeleteIds.length === 0) {
      return;
    }
    await Promise.allSettled(
      pendingDeleteIds.map((id) => deletePartFile(workOrderId, partId, id, userRole)),
    );
  }, [pendingDeleteIds, workOrderId, partId, userRole]);

  const rollback = useCallback(async (): Promise<void> => {
    if (sessionIds.length === 0) {
      return;
    }
    // Track the in-flight deletes so isBusy blocks close/re-cancel during rollback.
    setDeletingCount((c) => c + 1);
    try {
      await Promise.allSettled(
        sessionIds.map((id) => deletePartFile(workOrderId, partId, id, userRole)),
      );
    } finally {
      setDeletingCount((c) => c - 1);
    }
  }, [sessionIds, workOrderId, partId, userRole]);

  const getFreshUrl = useCallback(
    async (id: number): Promise<string> => {
      const files = await getPartFileUrls(workOrderId, partId, userRole);
      setServerFiles(files);
      return files.find((f) => f.id === id)?.url ?? '';
    },
    [workOrderId, partId, userRole],
  );

  const files = useMemo<TrackedPartFile[]>(() => {
    const pending = new Set(pendingDeleteIds);
    const session = new Set(sessionIds);
    const statusOf = (id: number): PartFileStatus => {
      if (pending.has(id)) return 'pending-delete';
      if (session.has(id)) return 'session';
      return 'saved';
    };
    const serverTracked: TrackedPartFile[] = serverFiles.map((f) => ({
      key: `server-${f.id}`,
      id: f.id,
      fileName: f.file_name,
      fileSize: f.file_size,
      createdAt: f.created_at,
      status: statusOf(f.id),
    }));
    const uploadingTracked: TrackedPartFile[] = uploading.map((u) => ({
      key: u.key,
      fileName: u.fileName,
      status: 'uploading',
    }));
    return [...serverTracked, ...uploadingTracked];
  }, [serverFiles, uploading, sessionIds, pendingDeleteIds]);

  return {
    files,
    loading,
    loadError,
    uploadingCount: uploading.length,
    isBusy: uploading.length > 0 || deletingCount > 0,
    canAddMore: serverFiles.length + uploading.length < MAX_FILE_COUNT,
    uploadFiles,
    requestDelete,
    restore,
    commit,
    rollback,
    getFreshUrl,
  };
}
